import { useEffect, useMemo, useState } from 'react'
import { createPortal } from 'react-dom'
import { apiFetch } from '../lib/api'
import { slugifySeoSegment } from '../lib/seoRoutes'

function getGoogleMapsUrl(restaurant) {
  const lat = Number(restaurant.latitude)
  const lng = Number(restaurant.longitude)
  if (Number.isFinite(lat) && Number.isFinite(lng)) {
    return `https://www.google.com/maps/search/?api=1&query=${lat},${lng}`
  }
  const query = [restaurant.name, restaurant.address || restaurant.area, restaurant.city].filter(Boolean).join(', ')
  return `https://www.google.com/maps/search/?api=1&query=${encodeURIComponent(query)}`
}

function formatReviewTime(value) {
  if (!value) return ''
  const date = new Date(value)
  return date.toLocaleDateString([], { month: 'short', day: 'numeric', year: 'numeric' })
}

export default function Restaurant({ currentUser, gpsCity, onOpenProfile, cityOverride, restaurantNameOverride }) {
  const [restaurants, setRestaurants] = useState([])
  const [loading, setLoading] = useState(true)
  const [selectedRestaurant, setSelectedRestaurant] = useState(null)
  const [error, setError] = useState('')
  const [restaurantLoading, setRestaurantLoading] = useState(false)
  const requestedRestaurantSlug = useMemo(() => slugifySeoSegment(restaurantNameOverride), [restaurantNameOverride])

  const cityName = useMemo(() => {
    return cityOverride || currentUser?.location?.city || gpsCity || 'Bengaluru'
  }, [cityOverride, currentUser, gpsCity])

  useEffect(() => {
    let active = true

    async function loadRestaurants() {
      setLoading(true)
      setError('')
      try {
        const res = await apiFetch(`/restaurants?city=${encodeURIComponent(cityName)}`)
        if (!res.ok) throw new Error('Unable to load restaurants')
        const data = await res.json()
        if (active) {
          setRestaurants(data)
          if (requestedRestaurantSlug) {
            const match = Array.isArray(data)
              ? data.find((restaurant) => slugifySeoSegment(restaurant.name) === requestedRestaurantSlug)
              : null
            if (match) {
              openRestaurant(match)
            }
          }
        }
      } catch (err) {
        if (active) setError(err.message || 'Unable to load restaurants right now.')
      } finally {
        if (active) setLoading(false)
      }
    }

    loadRestaurants()
    return () => { active = false }
  }, [cityName, requestedRestaurantSlug])

  async function openRestaurant(restaurant) {
    setSelectedRestaurant(restaurant)
    setRestaurantLoading(true)
    try {
      const res = await apiFetch(`/restaurants/${restaurant.id}`)
      if (!res.ok) throw new Error('Unable to load restaurant details')
      const data = await res.json()
      setSelectedRestaurant(current => (current?.id === restaurant.id ? data : current))
    } catch {
      setSelectedRestaurant(current => (current?.id === restaurant.id ? restaurant : current))
    } finally {
      setRestaurantLoading(false)
    }
  }

  function handleOpenProfile(userId) {
    if (!userId) return
    setSelectedRestaurant(null)
    onOpenProfile?.(userId)
  }

  return (
    <section className="space-y-6">
      <div className="fade-up flex flex-col gap-3 rounded-3xl border border-white/70 bg-white/80 p-6 shadow-xl backdrop-blur md:flex-row md:items-end md:justify-between">
        <div>
          <p className="text-sm font-semibold uppercase tracking-[0.2em] text-orange-600">Restaurants</p>
          <h2 className="title-font mt-1 text-2xl font-bold text-gray-900 md:text-3xl">Eat local in {cityName}</h2>
          <p className="mt-2 max-w-2xl text-sm text-gray-600">Browse nearby favorites by rating, vibe, and live opening status. Tap a card to open the full restaurant detail view.</p>
        </div>
        <div className="rounded-2xl bg-orange-50 px-4 py-3 text-sm text-orange-800 shadow-sm">
          {restaurants.length} restaurant{restaurants.length === 1 ? '' : 's'} ready to explore
        </div>
      </div>

      {error && (
        <div className="rounded-3xl border border-red-200 bg-red-50 px-4 py-3 text-sm text-red-700 shadow-sm">
          {error}
        </div>
      )}

      {loading ? (
        <div className="grid gap-4 md:grid-cols-2 xl:grid-cols-3">
          {Array.from({ length: 3 }).map((_, idx) => (
            <div key={idx} className="animate-pulse rounded-3xl border border-white/70 bg-white/80 p-5 shadow-xl">
              <div className="h-36 rounded-2xl bg-orange-100" />
              <div className="mt-4 h-4 w-24 rounded bg-gray-200" />
              <div className="mt-3 h-4 w-full rounded bg-gray-200" />
              <div className="mt-2 h-4 w-2/3 rounded bg-gray-200" />
            </div>
          ))}
        </div>
      ) : restaurants.length === 0 ? (
        <div className="rounded-3xl border border-white/70 bg-white/80 p-10 text-center shadow-xl">
          <p className="text-4xl">🍴</p>
          <h3 className="mt-3 text-xl font-semibold text-gray-900">No restaurants found for this city yet</h3>
          <p className="mt-1 text-sm text-gray-500">Try a different city from your profile or location settings.</p>
        </div>
      ) : (
        <div className="grid gap-5 md:grid-cols-2 xl:grid-cols-3">
          {restaurants.map(restaurant => (
            <button
              key={restaurant.id}
              type="button"
              onClick={() => openRestaurant(restaurant)}
              className="fade-up rounded-3xl border border-white/70 bg-white/80 p-4 text-left shadow-xl transition hover:-translate-y-1 hover:shadow-2xl"
            >
              <img src={restaurant.image} alt={restaurant.name} className="h-40 w-full rounded-2xl object-cover" />
              <div className="mt-4 flex items-start justify-between gap-3">
                <div>
                  <h3 className="text-xl font-semibold text-gray-900">{restaurant.name}</h3>
                  <p className="text-sm text-gray-500">{restaurant.area}, {restaurant.city}</p>
                </div>
                <span className={`rounded-full px-2.5 py-1 text-xs font-semibold ${restaurant.isOpen ? 'bg-emerald-100 text-emerald-700' : 'bg-amber-100 text-amber-700'}`}>
                  {restaurant.isOpen ? 'Open' : 'Closed'}
                </span>
              </div>

              <div className="mt-3 flex flex-wrap gap-2">
                {restaurant.tags?.slice(0, 3).map(tag => (
                  <span key={tag} className="rounded-full bg-orange-50 px-2.5 py-1 text-xs font-semibold text-orange-700">{tag}</span>
                ))}
              </div>

              <div className="mt-4 flex items-center justify-between text-sm text-gray-600">
                <span className="rounded-full bg-gray-100 px-2.5 py-1 font-semibold text-gray-700">⭐ {restaurant.rating.toFixed(1)}</span>
                <span>{restaurant.reviews} reviews</span>
              </div>

              <p className="mt-3 text-sm text-gray-600">{restaurant.openingStatus}</p>
              <p className="mt-1 text-xs text-gray-500">{restaurant.priceLevel} · {restaurant.distanceKm} km away</p>
            </button>
          ))}
        </div>
      )}

      {selectedRestaurant && createPortal(
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/45 p-4">
          <div className="max-h-[90vh] w-full max-w-4xl overflow-y-auto rounded-3xl bg-white shadow-2xl">
            <div className="grid gap-0 lg:grid-cols-[1.05fr_0.95fr]">
              <div className="p-4 sm:p-6 lg:p-8">
                <img src={selectedRestaurant.images?.[0] || selectedRestaurant.image} alt={selectedRestaurant.name} className="h-60 w-full rounded-3xl object-cover" />
                <div className="mt-4 flex flex-wrap gap-2">
                  {selectedRestaurant.tags?.map(tag => (
                    <span key={tag} className="rounded-full bg-orange-50 px-3 py-1 text-xs font-semibold text-orange-700">{tag}</span>
                  ))}
                </div>
                <h3 className="mt-4 title-font text-2xl font-bold text-gray-900">{selectedRestaurant.name}</h3>
                <p className="text-sm text-gray-500">{selectedRestaurant.area}, {selectedRestaurant.city}</p>
                <p className="mt-3 text-sm text-gray-700">{selectedRestaurant.description}</p>
                <div className="mt-4 flex flex-wrap gap-3 text-sm text-gray-700">
                  <span className="rounded-full bg-gray-100 px-3 py-1">⭐ {selectedRestaurant.rating.toFixed(1)}</span>
                  <span className="rounded-full bg-gray-100 px-3 py-1">🗣️ {selectedRestaurant.reviews} reviews</span>
                  <span className="rounded-full bg-gray-100 px-3 py-1">📍 {selectedRestaurant.distanceKm} km away</span>
                </div>
              </div>

              <div className="border-t border-gray-100 bg-gradient-to-b from-orange-50 to-white p-4 sm:p-6 lg:border-l lg:border-t-0 lg:p-8">
                <div className="flex items-start justify-between gap-3">
                  <div>
                    <p className="text-sm font-semibold uppercase tracking-[0.2em] text-orange-600">Restaurant details</p>
                    <h4 className="mt-1 text-xl font-bold text-gray-900">Why people love it</h4>
                  </div>
                  <button
                    type="button"
                    onClick={() => setSelectedRestaurant(null)}
                    className="rounded-full bg-white px-3 py-1.5 text-sm font-semibold text-gray-500 shadow-sm hover:text-gray-800"
                  >
                    Close
                  </button>
                </div>

                <a
                  href={getGoogleMapsUrl(selectedRestaurant)}
                  target="_blank"
                  rel="noopener noreferrer"
                  className="mt-5 inline-flex items-center gap-2 rounded-full bg-gray-900 px-4 py-2 text-sm font-semibold text-white shadow-sm hover:bg-black"
                >
                  📍 View on Map
                </a>

                <ul className="mt-5 space-y-3 text-sm text-gray-700">
                  <li><span className="font-semibold text-gray-900">Address:</span> {selectedRestaurant.address}</li>
                  <li><span className="font-semibold text-gray-900">Phone:</span> {selectedRestaurant.phone}</li>
                  <li><span className="font-semibold text-gray-900">Hours:</span> {selectedRestaurant.openingHours}</li>
                  <li><span className="font-semibold text-gray-900">Status:</span> {selectedRestaurant.openingStatus}</li>
                  <li><span className="font-semibold text-gray-900">Delivery:</span> {selectedRestaurant.delivery ? 'Available' : 'Not available'}</li>
                  <li><span className="font-semibold text-gray-900">Takeaway:</span> {selectedRestaurant.takeaway ? 'Available' : 'Not available'}</li>
                  <li><span className="font-semibold text-gray-900">Parking:</span> {selectedRestaurant.parking ? 'On-site' : 'No parking'}</li>
                </ul>

                <div className="mt-5 rounded-2xl bg-white p-4 shadow-sm">
                  <p className="text-sm font-semibold text-gray-900">Highlights</p>
                  <ul className="mt-3 space-y-2 text-sm text-gray-700">
                    {selectedRestaurant.highlights?.map(item => <li key={item}>• {item}</li>)}
                  </ul>
                </div>

                <div className="mt-5 rounded-2xl bg-white p-4 shadow-sm">
                  <div className="flex items-center justify-between gap-3">
                    <p className="text-sm font-semibold text-gray-900">Reviews</p>
                    {restaurantLoading && <span className="text-xs font-semibold text-orange-600">Loading…</span>}
                  </div>

                  {selectedRestaurant.feedbacks?.length ? (
                    <div className="mt-3 space-y-3">
                      {selectedRestaurant.feedbacks.map(review => (
                        <div key={review.id} className="rounded-2xl border border-orange-100 bg-orange-50/70 p-3">
                          <button
                            type="button"
                            onClick={() => handleOpenProfile(review.user?.id)}
                            disabled={!review.user?.id}
                            className="flex w-full items-center gap-3 text-left disabled:cursor-default"
                          >
                            <img
                              src={review.user?.profilePicture || `https://api.dicebear.com/7.x/thumbs/svg?seed=${encodeURIComponent(review.user?.name || 'Reviewer')}`}
                              alt={review.user?.name || 'Reviewer'}
                              className="h-11 w-11 rounded-full object-cover bg-white"
                            />
                            <div className="min-w-0 flex-1">
                              <p className="truncate text-sm font-semibold text-gray-900">{review.user?.name || 'Anonymous user'}</p>
                              <p className="text-xs text-gray-500">{formatReviewTime(review.createdAt)}</p>
                            </div>
                            <span className="rounded-full bg-white px-2.5 py-1 text-xs font-semibold text-orange-700">⭐ {Number(review.rating || 0).toFixed(1)}</span>
                          </button>
                          {review.comment && <p className="mt-3 text-sm leading-6 text-gray-700">{review.comment}</p>}
                        </div>
                      ))}
                    </div>
                  ) : (
                    <p className="mt-3 text-sm text-gray-500">No reviews yet. Be the first to share your experience.</p>
                  )}
                </div>

                <div className="mt-5 grid grid-cols-2 gap-3">
                  {selectedRestaurant.images?.slice(1).map(image => (
                    <img key={image} src={image} alt={selectedRestaurant.name} className="h-24 w-full rounded-2xl object-cover" />
                  ))}
                </div>
              </div>
            </div>
          </div>
        </div>,
        document.body,
      )}
    </section>
  )
}
